import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import PinEntry from './components/PinEntry';
import Dashboard from './components/Dashboard';
import LandingPage from './components/LandingPage';
import AdminPage from './components/AdminPage';
import { AppState } from './types';
import { validateUserPin } from './services/dataService';
import { Loader2 } from 'lucide-react';

const ACCESS_PREFIX = '/view/';

const AppContent: React.FC = () => {
  const [appState, setAppState] = useState<AppState>(AppState.LOADING);
  const [userId, setUserId] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;

    // 1. Landing Page Logic
    if (path === '/' || path === '') {
      // We are at the root, don't show the dashboard, show landing page
      setUserId(null);
      setAppState(AppState.LOADING); // Use loading as a placeholder, strictly handled by Routes in main App component
      return;
    }

    // 2. View User Logic
    if (path.startsWith(ACCESS_PREFIX)) {
      const extractedId = path.replace(ACCESS_PREFIX, '');
      if (extractedId) {
        setUserId(extractedId);
        
        // Check Session Persistence
        const sessionKey = `session_${extractedId}`;
        const hasSession = localStorage.getItem(sessionKey);
        
        if (hasSession) {
          setAppState(AppState.DASHBOARD);
        } else {
          setAppState(AppState.PIN_REQUIRED);
        }
      } else {
        setAppState(AppState.ERROR);
      }
    }
  }, [location.pathname]);

  const handlePinValidation = async (pin: string): Promise<boolean> => {
    if (!userId) return false;
    return await validateUserPin(userId, pin);
  };

  const handlePinSuccess = () => {
    if (userId) {
      localStorage.setItem(`session_${userId}`, 'true');
      setAppState(AppState.DASHBOARD);
    }
  };

  const handleLogout = () => {
    if (userId) {
      localStorage.removeItem(`session_${userId}`);
      setAppState(AppState.PIN_REQUIRED);
    }
  };

  // Logic to determine what to render inside the /view/:id route
  if (appState === AppState.ERROR) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-2xl font-bold text-slate-200 mb-2">Invalid Access Link</h1>
        <p className="text-slate-400 mb-8">Please check your URL.</p>
      </div>
    );
  }

  if (appState === AppState.PIN_REQUIRED && userId) {
    return <PinEntry secretId={userId} onValidate={handlePinValidation} onSuccess={handlePinSuccess} />;
  }

  if (appState === AppState.DASHBOARD && userId) {
    return <Dashboard userId={userId} onLogout={handleLogout} />;
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center space-y-4">
      <Loader2 className="w-10 h-10 text-cyan-400 animate-spin" />
      <p className="text-slate-400 text-sm font-medium">Loading App...</p>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/view/:id" element={<AppContent />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </HashRouter>
  );
};

export default App;